import { apiClient, handleApiError } from './api.js';

class AppointmentService {
  // Get all appointments
  async getAllAppointments() {
    try {
      return await apiClient.get('/appointment');
    } catch (error) {
      console.error('Error getting all appointments:', error);
      throw handleApiError(error);
    }
  }

  // Get appointment by ID
  async getAppointmentById(appointmentId) {
    try {
      return await apiClient.get(`/appointment/${appointmentId}`);
    } catch (error) {
      console.error(`Error getting appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Get appointments by patient ID
  async getAppointmentsByPatientId(patientId) {
    try {
      return await apiClient.get(`/appointment/patient/${patientId}`);
    } catch (error) {
      console.error(`Error getting appointments for patient ${patientId}:`, error);
      throw handleApiError(error);
    }
  }

  // Get appointments by physician ID
  async getAppointmentsByPhysicianId(physicianId) {
    try {
      return await apiClient.get(`/appointment/doctor/${physicianId}`);
    } catch (error) {
      console.error(`Error getting appointments for physician ${physicianId}:`, error);
      throw handleApiError(error);
    }
  }

  // Get appointments by date range
  async getAppointmentsByDateRange(startDate, endDate) {
    try {
      return await apiClient.get(`/appointment/date-range?startDate=${startDate}&endDate=${endDate}`);
    } catch (error) {
      console.error('Error getting appointments by date range:', error);
      throw handleApiError(error);
    }
  }

  // Get appointments by status
  async getAppointmentsByStatus(status) {
    try {
      return await apiClient.get(`/appointment/status/${status}`);
    } catch (error) {
      console.error(`Error getting appointments with status ${status}:`, error);
      throw handleApiError(error);
    }
  }

  // Search appointments
  async searchAppointments(searchTerm) {
    try {
      return await apiClient.get(`/appointment/search?term=${searchTerm}`);
    } catch (error) {
      console.error('Error searching appointments:', error);
      throw handleApiError(error);
    }
  }

  // Create a new appointment
  async createAppointment(appointment) {
    try {
      // Format date if needed - use timezone-safe formatting
      const appointmentDate = typeof appointment.date === 'string' 
        ? appointment.date 
        : appointment.date.getFullYear() + '-' + 
          String(appointment.date.getMonth() + 1).padStart(2, '0') + '-' + 
          String(appointment.date.getDate()).padStart(2, '0');

      // Calculate EndTime based on StartTime and Duration
      let endTime = '';
      if (appointment.time && appointment.duration) {
        const [hours, minutes] = appointment.time.split(':').map(Number);
        const startTimeMinutes = hours * 60 + minutes;
        const endTimeMinutes = startTimeMinutes + (appointment.duration || 30);
        const endHours = Math.floor(endTimeMinutes / 60) % 24;
        const endMinutes = endTimeMinutes % 60;
        endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
      }

      // Prepare appointment data with proper casing to match backend model
      const appointmentData = {
        AppointmentId: 0, // Will be assigned by the backend
        PatientId: appointment.patientId,
        DoctorId: parseInt(appointment.physicianId) || null, // Convert physicianId to integer for DoctorId field
        AppointmentDate: appointmentDate,
        StartTime: appointment.time || '', // Add StartTime field from the time property
        EndTime: endTime, // Add calculated EndTime
        Duration: appointment.duration || 30,
        Status: appointment.status || 'Scheduled',
        Title: appointment.title || `Appointment on ${appointmentDate}`,
        Description: appointment.description || appointment.notes || '',
        IsHouseCall: !!appointment.isHouseCall,
        ServiceAddress: appointment.serviceAddress || '',
        ServiceCity: appointment.serviceCity || '',
        ServiceState: appointment.serviceState || '',
        ServicePostalCode: appointment.servicePostalCode || '',
        ServiceLatitude: typeof appointment.serviceLatitude === 'number' ? appointment.serviceLatitude : null,
        ServiceLongitude: typeof appointment.serviceLongitude === 'number' ? appointment.serviceLongitude : null,
        IsRecurring: appointment.isRecurring || false,
        PhysicianName: appointment.physician || '' // Include physician name for display purposes
        // No need to include CreatedBy, CreatedAt, etc. as they're handled by the backend
      };
      
      const response = await apiClient.post('/appointment', appointmentData);
      return response;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw handleApiError(error);
    }
  }
  
  // Create a new appointment with a new patient
  async createAppointmentWithNewPatient(appointment, patient) {
    try {
      // Format date if needed - use timezone-safe formatting
      const appointmentDate = typeof appointment.date === 'string' 
        ? appointment.date 
        : appointment.date.getFullYear() + '-' + 
          String(appointment.date.getMonth() + 1).padStart(2, '0') + '-' + 
          String(appointment.date.getDate()).padStart(2, '0');

      // Calculate EndTime based on StartTime and Duration
      let endTime = '';
      if (appointment.time && appointment.duration) {
        const [hours, minutes] = appointment.time.split(':').map(Number);
        const startTimeMinutes = hours * 60 + minutes;
        const endTimeMinutes = startTimeMinutes + (appointment.duration || 30);
        const endHours = Math.floor(endTimeMinutes / 60) % 24;
        const endMinutes = endTimeMinutes % 60;
        endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
      }


      
      // Prepare appointment data with proper casing to match backend model
      const appointmentData = {
        Appointment: {
          AppointmentDate: appointmentDate,
          StartTime: appointment.time || '',
          EndTime: endTime, // Add calculated EndTime
          Duration: appointment.duration || 30, // Send duration instead of EndTime
          DoctorId: parseInt(appointment.physicianId) || null, // Convert physicianId to integer for DoctorId field
          Notes: appointment.description || appointment.notes || '',
          Status: appointment.status || 'Scheduled',
          AppointmentType: appointment.type || '',
          IsHouseCall: !!appointment.isHouseCall,
          ServiceAddress: appointment.serviceAddress || '',
          ServiceCity: appointment.serviceCity || '',
          ServiceState: appointment.serviceState || '',
          ServicePostalCode: appointment.servicePostalCode || '',
          ServiceLatitude: typeof appointment.serviceLatitude === 'number' ? appointment.serviceLatitude : null,
          ServiceLongitude: typeof appointment.serviceLongitude === 'number' ? appointment.serviceLongitude : null,
          IsRecurring: appointment.isRecurring || false,
          RecurrenceInfoId: appointment.recurrenceInfoId || null
        },
        Patient: {
          // Only nickname and phone number are required for initial appointment scheduling
          // The rest will be collected when the patient visits the clinic
          Nickname: patient.nickname || '',
          PhoneNumber: patient.phoneNumber || '',
          
          // These fields are required by the backend but will be filled with placeholders
          // and updated later when the patient visits
          FirstName: patient.firstName || 'To be updated',
          LastName: patient.lastName || 'To be updated',
          Gender: patient.gender || 'Unknown',
          Email: patient.email || 'pending@example.com',
          Address: patient.address || 'To be updated',
          
          // Optional fields that will be updated later
          DateOfBirth: patient.dateOfBirth || null,
          EmergencyContactName: patient.emergencyContactName || 'To be updated',
          EmergencyContactPhone: patient.emergencyContactPhone || 'To be updated',
          InsuranceProvider: patient.insuranceProvider || 'To be updated',
          InsurancePolicyNumber: patient.insurancePolicyNumber || 'To be updated',
          MedicalRecordNumber: patient.medicalRecordNumber || 'To be updated',
          OfficialDocumentNumber: patient.officialDocumentNumber || 'To be updated',
          City: patient.city || '',
          State: patient.state || '',
          PostalCode: patient.postalCode || ''
        }
      };
      

      
      const response = await apiClient.post('/appointment/with-new-patient', appointmentData);
      return response;
    } catch (error) {
      console.error('Error creating appointment with new patient:', error);
      throw handleApiError(error);
    }
  }

  // Create a recurring appointment series
  async createRecurringAppointment(appointment) {
    try {
      // Format date for the API - use timezone-safe formatting
      const appointmentDate = typeof appointment.date === 'string' 
        ? appointment.date 
        : appointment.date.getFullYear() + '-' + 
          String(appointment.date.getMonth() + 1).padStart(2, '0') + '-' + 
          String(appointment.date.getDate()).padStart(2, '0');
      
      // Calculate EndTime based on StartTime and Duration
      let endTime = '';
      if (appointment.time && appointment.duration) {
        const [hours, minutes] = appointment.time.split(':').map(Number);
        const startTimeMinutes = hours * 60 + minutes;
        const endTimeMinutes = startTimeMinutes + (appointment.duration || 30);
        const endHours = Math.floor(endTimeMinutes / 60) % 24;
        const endMinutes = endTimeMinutes % 60;
        endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
      }
      
      // Prepare appointment data with recurrence information exactly as expected by the backend
      const appointmentData = {
        // Create a proper Appointment object as required by the backend model
        Appointment: {
          AppointmentId: 0, // Will be assigned by the backend
          PatientId: appointment.patientId,
          DoctorId: parseInt(appointment.physicianId) || null, // Convert physicianId to integer for DoctorId field
          AppointmentDate: appointmentDate,
          StartTime: appointment.time || '', // Add StartTime field from the time property
          EndTime: endTime, // Add calculated EndTime
          Duration: appointment.duration || 30,
          Status: appointment.status || 'Scheduled',
          Title: appointment.title || `Appointment on ${appointmentDate}`,
          Description: appointment.description || appointment.notes || '',
          IsHouseCall: !!appointment.isHouseCall,
          ServiceAddress: appointment.serviceAddress || '',
          ServiceCity: appointment.serviceCity || '',
          ServiceState: appointment.serviceState || '',
          ServicePostalCode: appointment.servicePostalCode || '',
          ServiceLatitude: typeof appointment.serviceLatitude === 'number' ? appointment.serviceLatitude : null,
          ServiceLongitude: typeof appointment.serviceLongitude === 'number' ? appointment.serviceLongitude : null,
          IsRecurring: true,
          // Add required navigation properties to satisfy backend validation
          Patient: { PatientId: appointment.patientId }, // Full Patient object required by backend
          Doctor: { 
            DoctorId: parseInt(appointment.physicianId) || 0, // Convert physicianId to integer for DoctorId field
            Name: appointment.physician || '' // Include physician name for display purposes
          }, // Full Doctor object required by backend but keeping Doctor name for compatibility
          CreatedByUser: { UserId: 0 }, // Will be populated by backend from JWT token
          UpdatedByUser: { UserId: 0 }, // Will be populated by backend from JWT token
          RecurrenceInfo: { RecurrenceInfoId: 0 } // RecurrenceInfo object with ID to satisfy validation
        },
        // Recurrence properties at the root level as expected by the backend
        RecurrenceType: appointment.recurrenceType || 'Weekly',
        Interval: appointment.interval || 1,
        DaysOfWeek: appointment.daysOfWeek || '',
        EndDate: appointment.endDate ? new Date(appointment.endDate).toISOString() : null,
        Occurrences: appointment.occurrences || 4,
        // Flag to indicate that child appointments should be created
        CreateChildAppointments: true
      };
      
      const response = await apiClient.post('/appointment/recurring', appointmentData);
      
      // Process the response to extract any skipped holidays
      if (response.data && Array.isArray(response.data)) {
        // The backend returns an array of appointments
        // We need to check if there's any metadata about skipped holidays
        const metadata = response.data.find(item => item.skippedHolidays);
        if (metadata && metadata.skippedHolidays) {
          response.data.skippedHolidays = metadata.skippedHolidays;
        }
      }
      
      return response;
    } catch (error) {
      console.error('Error creating recurring appointment:', error);
      throw handleApiError(error);
    }
  }

  // Check if an appointment is part of a recurring series
  async isRecurringAppointment(appointmentId) {
    try {
      const response = await apiClient.get(`/appointment/${appointmentId}/is-recurring`);
      return response.data;
    } catch (error) {
      console.error(`Error checking if appointment ${appointmentId} is recurring:`, error);
      throw handleApiError(error);
    }
  }

  // Get all child appointments for a recurring appointment
  async getRecurringAppointmentChildren(parentAppointmentId) {
    try {
      const response = await apiClient.get(`/appointment/recurring/${parentAppointmentId}/children`);
      return response.data;
    } catch (error) {
      console.error(`Error getting child appointments for recurring appointment ${parentAppointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Update appointment status
  async updateAppointmentStatus(appointmentId, status) {
    try {
      const response = await apiClient.patch(`/appointment/${appointmentId}/status`, { status });
      return response;
    } catch (error) {
      console.error(`Error updating status for appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Update status for a recurring appointment (with option to update entire series)
  async updateRecurringAppointmentStatus(appointmentId, status, updateEntireSeries = false) {
    try {
      const response = await apiClient.patch(`/appointment/recurring/${appointmentId}/status`, {
        status,
        updateEntireSeries
      });
      return response;
    } catch (error) {
      console.error(`Error updating status for recurring appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Update an existing appointment
  async updateAppointment(appointmentId, appointment) {
    try {
      // Format date if needed - use timezone-safe formatting
      const appointmentDate = typeof appointment.date === 'string' 
        ? appointment.date 
        : appointment.date.getFullYear() + '-' + 
          String(appointment.date.getMonth() + 1).padStart(2, '0') + '-' + 
          String(appointment.date.getDate()).padStart(2, '0');

      // Calculate EndTime based on StartTime and Duration
      let endTime = '';
      if (appointment.time && appointment.duration) {
        const [hours, minutes] = appointment.time.split(':').map(Number);
        const startTimeMinutes = hours * 60 + minutes;
        const endTimeMinutes = startTimeMinutes + (appointment.duration || 30);
        const endHours = Math.floor(endTimeMinutes / 60) % 24;
        const endMinutes = endTimeMinutes % 60;
        endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
      }

      // The backend will handle timestamp tracking and user tracking via the JWT token
      // UpdatedBy and UpdatedAt are extracted from the JWT token in the backend
      const appointmentData = {
        ...appointment,
        AppointmentId: appointmentId,
        DoctorId: parseInt(appointment.physicianId) || 0, // Convert physicianId to integer for DoctorId field
        AppointmentDate: appointmentDate,
        StartTime: appointment.time || '', // Ensure StartTime is set
        EndTime: endTime, // Add calculated EndTime
        PhysicianName: appointment.physician || '', // Include physician name for display purposes
        // Initialize navigation property for user tracking
        // This will be populated by the backend based on the JWT token
        // Providing empty User object with UserId property to satisfy model validation
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.put(`/appointment/${appointmentId}`, appointmentData);
    } catch (error) {
      console.error(`Error updating appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Delete an appointment
  async deleteAppointment(appointmentId) {
    try {
      return await apiClient.delete(`/appointment/${appointmentId}`);
    } catch (error) {
      console.error(`Error deleting appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Check appointment availability
  async checkAvailability(physicianId, date) {
    try {
      return await apiClient.get(`/appointment/availability/${physicianId}?date=${date}`);
    } catch (error) {
      console.error(`Error checking availability for physician ${physicianId}:`, error);
      throw handleApiError(error);
    }
  }

  // Update appointment status
  async updateAppointmentStatus(appointmentId, status) {
    try {
      return await apiClient.put(`/appointment/${appointmentId}/status`, { status });
    } catch (error) {
      console.error(`Error updating status for appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Get recurring appointment information
  async getRecurringAppointmentInfo(appointmentId) {
    try {
      return await apiClient.get(`/appointment/${appointmentId}/recurring-info`);
    } catch (error) {
      console.error(`Error getting recurring info for appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Check if appointment is part of a recurring series
  async isRecurringAppointment(appointmentId) {
    try {
      const response = await apiClient.get(`/appointment/${appointmentId}/is-recurring`);
      return response.data.isRecurring;
    } catch (error) {
      console.error(`Error checking if appointment ${appointmentId} is recurring:`, error);
      throw handleApiError(error);
    }
  }

  // Update recurring appointment status
  async updateRecurringAppointmentStatus(appointmentId, status, updateSeries) {
    try {
      return await apiClient.put(`/appointment/${appointmentId}/recurring-status`, { 
        status, 
        updateSeries 
      });
    } catch (error) {
      console.error(`Error updating recurring status for appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }
}

export default new AppointmentService();